// Settings › Markup & fitting (design/screens/settings-markups.html). Its pricing part (UC-PRICING-004..006, -009): the line that
// says how bands resolve, the tenant's bands with an example price each and the acting branch's overrides, with Add band, Edit,
// Retire and Copy from tenant default for an Owner or Admin (MarkupBands.tsx); the lens labs' price lists with End on a branch's
// own (PriceLists.tsx); the pricing settings as the acting branch resolves them, and every product markup (PricingSettings.tsx).
// Every role reads all of it; the writes are the D38 matrix's (BR-PRICING-024) — a Branch Owner changes only their branch's
// pricing settings (D40). The prototype's "Recent overrides at purchase" is the acting branch's recent overrides at receipt — the
// prices kept at a goods receipt apart from their proposal (UC-PRICING-007 A6, A7; GoodsReceipts.tsx) — and its fitting fees,
// frame-type surcharges and default fitting lab wait for orders and lab orders (the board moved them). Its Catalogue
// settings panel (UC-CATALOGUE-011; /settings/markups/catalogue): the organisation's two catalogue switches with where each value
// comes from, saved through `/catalogue-settings` (`catalogue.settings.write`: Owner, Admin). A branch's override of them is set
// in that branch's settings — Settings › Branches, the branch selected (BranchCatalogueSettings.tsx; UC-TENANCY-007, D40) — not here.
import type { CatalogueSettingsView, FrameMarkupBandView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { catalogueSettings } from "../api/endpoints.catalogue";
import { pricing } from "../api/endpoints.pricing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, ErrorAlert, Loading, Panel } from "../ui/bits";
import { Icon } from "../ui/icons";
import { activeBranchName } from "./CatalogueShared";
import { NoAccessPanel, PageHead, SettingsTabs } from "./common";
import { RecentReceiptOverridesPanel } from "./GoodsReceipts";
import { BandDialog, OverridesPanel, RetireBandDialog, TenantBandsPanel, resolutionLine, splitBands, type BandActions } from "./MarkupBands";
import { PriceListsPanel } from "./PriceLists";
import { PricingSettingsPanel, ProductMarkupsPanel } from "./PricingSettings";
import { countWord, isPlain, ruleOf, usePricingSettings } from "./PricingShared";

const SOURCE_WORD: Readonly<Record<string, string>> = { default: "default", tenant: "organisation", branch: "branch" };
/** A band write carried out with its answer not kept (`409 REQUEST_COMPLETED`, BR-GOVERNANCE-016): what the page then says. */
const BANDS_READ_AGAIN = "Its answer did not arrive, so the bands have been read again.";
const SWITCHES: readonly { key: "framesSerialisedByDefault" | "importCreatesNonStockedLenses"; label: string; help: string }[] = [
  { key: "framesSerialisedByDefault", label: "Frames start serialised", help: "Whether a new frame starts with Serialised ticked — every unit then gets its own serial when it arrives. Products already added keep their flags." },
  { key: "importCreatesNonStockedLenses", label: "Lenses an import creates start made to order", help: "Whether a lens a price-list import creates holds no stock of its own. Lenses already added keep their flags." },
];

function MarkupsTabs() {
  const s = useSessionView();
  const loc = useLocation();
  return (
    <div className="tabs" aria-label="Markup & fitting">
      <NavLink to="/settings/markups" end className={loc.pathname === "/settings/markups" ? "active" : ""}>Markups and rounding</NavLink>
      {can(s, "catalogue.settings.write") && <NavLink to="/settings/markups/catalogue" className={loc.pathname.startsWith("/settings/markups/catalogue") ? "active" : ""}>Catalogue settings</NavLink>}
    </div>
  );
}

/**
 * The tab's pricing part. The page is about the acting branch: its overrides, its pricing settings as they resolve there; Add band
 * offers that branch first, and Copy from tenant default copies to it. At a practice of one store the bands are the organisation's
 * alone — no Overrides panel, no line on how bands resolve, no Copy from tenant default, no scope on Add band — unless the store
 * holds bands of its own, added before, which stay listed with the line so they can be retired (UC-PRICING-004 A7; DVN's answers of
 * 28 Oct 2026, change 13).
 */
export function SettingsMarkups() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const title = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const canBands = can(s, "pricing.markup.write");
  const branchId = s?.activeBranchId ?? null;
  const branchName = activeBranchName(s) ?? "this branch";
  const branches = (s?.switchableBranches ?? []).filter((b) => b.status === "Active");
  const bands = useQuery({ queryKey: ["pricing", "bands"], queryFn: () => pricing.bands() });
  const orgSettings = usePricingSettings("org");
  const all = useMemo(() => bands.data?.data.items ?? [], [bands.data]);
  const { tenantPlain, tenantKeyed, branch } = useMemo(() => splitBands(all, branchId), [all, branchId]);
  const oneStore = useOneStore();
  const showBranch = !oneStore || branch.length > 0;
  const [dialog, setDialog] = useState<{ kind: "add" } | { kind: "edit" | "retire"; band: FrameMarkupBandView } | null>(null);
  // A discarded price-list import comes back here saying so (UC-IMPORTS-006 step 5); the notice is said once, not on a reload.
  const location = useLocation();
  const navigate = useNavigate();
  const passed = (location.state ?? null) as { notice?: string } | null;
  const [notice, setNotice] = useState<{ tone: "ok" | "crit"; text: ReactNode } | null>(passed?.notice ? { tone: "ok", text: <b>{passed.notice}</b> } : null);
  useEffect(() => {
    if (passed?.notice) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [copying, setCopying] = useState(false);
  const [copyKey, setCopyKey] = useState(() => newIdempotencyKey());
  useEffect(() => { setCopyKey(newIdempotencyKey()); }, [branchId]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["pricing"] });
  const actions: BandActions = {
    canWrite: canBands, disabled: block !== null, title,
    onEdit: (b) => { setNotice(null); setDialog({ kind: "edit", band: b }); },
    onRetire: (b) => { setNotice(null); setDialog({ kind: "retire", band: b }); },
  };
  // UC-PRICING-004 A4, E4: only the tenant's bands without a key are copied, and only to a branch with none of its own.
  const copyBlocked = branch.some(isPlain) ? `${branchName} already has its own bands`
    : tenantPlain.length === 0 ? "The organisation has no bands to copy" : branchId === null ? "Choose a branch in the top bar" : null;
  const copy = async () => {
    if (!branchId || copying) return;
    setCopying(true);
    setNotice(null);
    try {
      const { data } = await pricing.copyTenantBands(branchId, copyKey);
      setCopyKey(newIdempotencyKey());
      setNotice({ tone: "ok", text: <><b>{branchName} has its own copy of the {countWord(data.bands.length)} tenant band{data.bands.length === 1 ? "" : "s"}.</b> Change any of them under Overrides — {branchName}; no price already set changes.</> });
      await refresh();
    } catch (err) {
      // Pressed again under its key after a lost answer and answered as carried out, its answer not kept (BR-GOVERNANCE-016): the
      // bands were copied; they are read again, and a further copy is a new request (UC-PRICING-004 A6).
      if (carriedOut(err)) {
        setCopyKey(newIdempotencyKey());
        setNotice({ tone: "ok", text: <><b>{branchName} has its own copy of the tenant bands.</b> {BANDS_READ_AGAIN}</> });
        await refresh();
        return;
      }
      setNotice({ tone: "crit", text: <><b>Not copied.</b> {isApiError(err) && err.field("branchId")?.code === "overlap" ? `${branchName} already has its own bands.` : describeError(err)}</> });
    } finally { setCopying(false); }
  };
  return (
    <section className="screen" id="s-settings-markups">
      <div className="content">
        <PageHead title="Markup & fitting" sub={`${branchName} · frame markup bands applied to supplier cost at goods receipt · lens lab price lists`} actions={canBands && <>
          {!oneStore && <button className="btn" type="button" disabled={block !== null || copyBlocked !== null || copying} title={title ?? copyBlocked ?? undefined} onClick={() => void copy()}>{copying ? "Copying…" : "Copy from tenant default"}</button>}
          <button className="btn primary" type="button" disabled={block !== null} title={title} onClick={() => { setNotice(null); setDialog({ kind: "add" }); }}><Icon name="plus" />Add band</button>
        </>} />
        <SettingsTabs />
        <MarkupsTabs />
        {notice && <Alert tone={notice.tone} actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice.text}</Alert>}
        {bands.isPending && <Loading what="the markup bands" />}
        {bands.isError && <ErrorAlert error={bands.error} />}
        {bands.data && <>
          {showBranch && <Alert tone="info">{resolutionLine(branchName, branch)}</Alert>}
          {/* One above the other at every width: with Edit and Retire on each row the two tables do not fit side by side. */}
          <div className="stack" style={{ gap: 16 }}>
            <TenantBandsPanel plain={tenantPlain} keyed={tenantKeyed} rule={ruleOf(orgSettings.data?.data.settings, "roundingRule")} actions={actions} oneStore={oneStore} />
            {showBranch && <OverridesPanel branchName={branchName} bands={branch} actions={actions} />}
          </div>
          <RecentReceiptOverridesPanel branchId={branchId} branchName={branchName} bands={all} />
        </>}
        <PriceListsPanel />
        <div className="grid-2">
          <PricingSettingsPanel branchId={branchId} branchName={branchId ? branchName : null} />
          <ProductMarkupsPanel />
        </div>
        <p className="help">Fitting fees, frame-type surcharges and the default fitting lab are not built yet — they come with orders and lab orders (Sprint 5 or Sprint 7).</p>
        {dialog?.kind === "add" && (
          <BandDialog band={null} branches={branches} defaultScope={branchId ?? "org"} known={all} oneStore={oneStore} onClose={() => setDialog(null)} onSaved={(b) => {
            setDialog(null);
            setNotice({ tone: "ok", text: b ? <><b>{oneStore && !b.branch ? "Band added." : `Band added ${b.branch ? `for ${b.branch.name}` : "for every branch"}.`}</b> Later proposals use it; no price already set changes.</> : <><b>Band added.</b> {BANDS_READ_AGAIN}</> });
            void refresh();
          }} />
        )}
        {dialog?.kind === "edit" && (
          <BandDialog band={dialog.band} branches={branches} defaultScope={dialog.band.branchId ?? "org"} known={all} oneStore={oneStore} onClose={() => setDialog(null)} onSaved={(b) => {
            setDialog(null);
            setNotice({ tone: "ok", text: b ? <><b>Band saved.</b> Later proposals use it; no price already set changes.</> : <><b>Band saved.</b> {BANDS_READ_AGAIN}</> });
            void refresh();
          }} />
        )}
        {dialog?.kind === "retire" && (
          <RetireBandDialog band={dialog.band} oneStore={oneStore} onClose={() => { setDialog(null); void refresh(); }} onRetired={(answerLost) => {
            setDialog(null);
            setNotice({ tone: "ok", text: answerLost ? <><b>Band retired.</b> {BANDS_READ_AGAIN}</> : <><b>Band retired.</b> Frames it covered fall to the next band; it is kept with its history.</> });
            void refresh();
          }} />
        )}
      </div>
    </section>
  );
}

export function SettingsCatalogue() {
  const s = useSessionView();
  const qc = useQueryClient();
  // A practice of one store: the organisation's values are its one branch's, named with no word of a branch's own (UC-CATALOGUE-011 A6).
  const oneStore = useOneStore();
  const block = useWriteBlock();
  const online = block === null;
  const allowed = can(s, "catalogue.settings.write");
  const q = useQuery({ queryKey: ["catalogue-settings"], queryFn: () => catalogueSettings.get(), enabled: allowed });
  const [view, setView] = useState<CatalogueSettingsView | null>(null);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (q.data) setView(q.data.data); }, [q.data]);
  const valueOf = (key: string): boolean => (key in draft ? draft[key]! : view?.catalogue[key]?.value !== false);
  const changed = SWITCHES.filter((w) => view && valueOf(w.key) !== (view.catalogue[w.key]?.value !== false));
  const save = async () => {
    if (busy || !view) return;
    setSaved(false);
    // A save that changes nothing sends nothing (UC-CATALOGUE-011 A2).
    if (changed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await catalogueSettings.patch({ catalogue: Object.fromEntries(changed.map((w) => [w.key, valueOf(w.key)])) });
      setView(data);
      setDraft({});
      setSaved(true);
      qc.setQueryData(["catalogue-settings"], (old: unknown) => (old && typeof old === "object" ? { ...(old as object), data } : old));
      void qc.invalidateQueries({ queryKey: ["settings", "effective"] });
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  const denied = !allowed || (q.isError && isApiError(q.error) && q.error.code === "PERMISSION_DENIED");
  return (
    <section className="screen" id="s-settings-catalogue">
      <div className="content">
        <PageHead title="Markup & fitting" sub="organisation-wide settings" />
        <SettingsTabs />
        <MarkupsTabs />
        {denied ? <NoAccessPanel page="Catalogue settings" error={q.error} permission="catalogue.settings.write" /> : <>
          {q.isPending && <Loading what="the catalogue settings" />}
          {q.isError && <ErrorAlert error={q.error} />}
          {saved && <Alert tone="ok"><b>Saved.</b> {oneStore ? "It applies within a minute" : "Every branch without its own value follows within a minute"}; products already added keep their flags.</Alert>}
          {error !== null && <Alert tone="crit"><b>Not saved.</b> {describeError(error)}</Alert>}
          {view && (
            <Panel title="Catalogue settings" meta={oneStore ? "the organisation's values" : "the organisation's values · a branch's own is set under Settings › Branches"}
              foot={<>
                <span className="help">Rounding and whether a receipt proposes a price are <Link to="/settings/markups">Pricing settings</Link>. The VAT rate is the organisation's, under <Link to="/settings/general">General</Link>.</span>
                <button className="btn primary right" type="button" disabled={busy || !online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
              </>}>
              <div className="list">
                {SWITCHES.map((w) => {
                  const on = valueOf(w.key);
                  const source = view.catalogue[w.key]?.source ?? "default";
                  return (
                    <div key={w.key} className="item">
                      <div className="grow"><div className="primary">{w.label}</div><div className="sub">{w.help}</div></div>
                      <span className="small"><b>{on ? "On" : "Off"}</b> · <span className="faint">{w.key in draft ? "changed, not saved" : SOURCE_WORD[source] ?? source}</span></span>
                      <button type="button" className={`toggle${on ? " on" : ""}`} role="switch" aria-checked={on} aria-label={w.label} disabled={!online}
                        onClick={() => { setSaved(false); setDraft((d0) => { const next = { ...d0, [w.key]: !on }; if (view && next[w.key] === (view.catalogue[w.key]?.value !== false)) delete next[w.key]; return next; }); }} />
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
        </>}
      </div>
    </section>
  );
}
